import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentStore {
  // State
  preferences: CookiePreferences | null;
  bannerVisible: boolean;
  customizeMode: boolean;

  // Actions
  acceptAll: () => void;
  rejectOptional: () => void;
  savePreferences: (prefs: CookiePreferences) => Promise<void>;
  showBanner: () => void;
  hideBanner: () => void;
  toggleCustomize: () => void;
  loadPreferences: () => void;
}

/**
 * Cookie Consent Store - Manages cookie preferences and banner visibility
 * PIPEDA Compliance: Users must explicitly consent to non-essential cookies
 *
 * Preferences are:
 * 1. Stored in localStorage (client-side)
 * 2. Synced to backend (if authenticated) via /api/users/me/cookie-preferences
 */
export const useCookieConsentStore = create<CookieConsentStore>()(
  persist(
    (set, get) => ({
      preferences: null,
      bannerVisible: false,
      customizeMode: false,

      acceptAll: () => {
        const prefs: CookiePreferences = {
          essential: true,
          functional: true,
          analytics: true,
          marketing: true,
        };
        get().savePreferences(prefs);
      },

      rejectOptional: () => {
        const prefs: CookiePreferences = {
          essential: true,
          functional: false,
          analytics: false,
          marketing: false,
        };
        get().savePreferences(prefs);
      },

      savePreferences: async (prefs: CookiePreferences) => {
        // Ensure essential is always true (required for site functionality)
        const finalPrefs = { ...prefs, essential: true };

        // Save to backend (if authenticated)
        try {
          const response = await fetch('/api/users/me/cookie-preferences', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ preferences: finalPrefs }),
          });

          // 401 means user is not authenticated - this is OK, we still save locally
          if (!response.ok && response.status !== 401) {
            console.warn('Failed to sync cookie preferences to backend');
          }
        } catch (error) {
          // Network error - graceful fallback to localStorage only
          console.warn('Failed to sync cookie preferences', error);
        }

        // Update state (will trigger persist to localStorage)
        set({
          preferences: finalPrefs,
          bannerVisible: false,
          customizeMode: false,
        });
      },

      showBanner: () => set({ bannerVisible: true }),
      hideBanner: () => set({ bannerVisible: false }),
      toggleCustomize: () => set((state) => ({ customizeMode: !state.customizeMode })),

      loadPreferences: () => {
        const stored = localStorage.getItem('cookie-consent');
        if (!stored) {
          // First visit - show banner
          set({ bannerVisible: true });
        } else {
          try {
            const data = JSON.parse(stored);
            if (data.state?.preferences) {
              set({ preferences: data.state.preferences, bannerVisible: false });
            } else {
              set({ bannerVisible: true });
            }
          } catch {
            // Invalid data - show banner
            set({ bannerVisible: true });
          }
        }
      },
    }),
    {
      name: 'cookie-consent',
      partialize: (state) => ({ preferences: state.preferences }),
    }
  )
);
